import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  deleteModalRecoil,
  modelReportModalRecoil,
  modelModifyModalRecoil,
} from '@/atom/ModalAtom';
import { modelDetailRecoil } from '@/atom/ModelAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import TitleTextBox from '@/component/TitleTextBox';
import DeleteModal from '@/modal/DeleteModal';
import { ModelApi } from '@/sdk/ModelApi';
import { DeleteModelDtoTx } from '@/sdk/data-contracts';
import ModelReportModal from '@/modal/ModelReportModal';
import ModelModifyModal from '@/modal/ModelModifyModal';

const ModelDetail = () => {
  const { id } = useParams<{ id: string }>();
  const modelApi = new ModelApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const [deleteModalState, setDeleteModalState] =
    useRecoilState(deleteModalRecoil);
  const [modelReportModalState, setModelReportModalState] = useRecoilState(
    modelReportModalRecoil,
  );
  const [modelModifyModalState, setModelModifyModalState] = useRecoilState(
    modelModifyModalRecoil,
  );
  const [modelDetailState, setModelDetailState] =
    useRecoilState(modelDetailRecoil);

  const fetchModelDetail = async () => {
    try {
      const { data } = await modelApi.modelControllerGetModel(Number(id), {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setModelDetailState(data);
    } catch (err: any) {}
  };

  const handleModelDelete = async () => {
    try {
      const dto: DeleteModelDtoTx = {
        ids: [Number(id)],
      };
      await modelApi.modelControllerDeleteModel(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      navigate('/model');
    } catch (err: any) {}
  };

  useEffect(() => {
    fetchModelDetail();
  }, []);

  return (
    <>
      <CntBox1>
        <div className="flex aic jcsb">
          <TitleTextBox text="모델 상세정보" />
          <BS.BtnBox1>
            <BS.Btn1Type1
              type="button"
              onClick={() => setModelReportModalState(true)}
            >
              리포트 관리
            </BS.Btn1Type1>
            <BS.Btn1Type1 type="button" onClick={() => navigate('/model')}>
              목록
            </BS.Btn1Type1>
            <BS.Btn1Type1
              type="button"
              onClick={() => setModelModifyModalState(true)}
            >
              수정
            </BS.Btn1Type1>
            <BS.Btn1Type2 type="button" onClick={handleModelDelete}>
              삭제
            </BS.Btn1Type2>
          </BS.BtnBox1>
        </div>
        <InforWrap>
          <InforBox>
            <h1>모델 기본정보</h1>
            <div className="text-wrap">
              <ul className="text-box">
                <li>
                  <h2 style={{ minWidth: 90 }}>모델명</h2>
                  <p>{modelDetailState?.name}</p>
                </li>
                <li>
                  <h2 style={{ minWidth: 90 }}>전송주기</h2>
                  <p>{modelDetailState?.transmissionCycle}</p>
                </li>
                <li>
                  <h2 style={{ minWidth: 90 }}>App EUI</h2>
                  <p>{modelDetailState?.appEUI}</p>
                </li>
                <li>
                  <h2 style={{ minWidth: 90 }}>모델 설명</h2>
                  <p>{modelDetailState?.description}</p>
                </li>
              </ul>
              <ul className="text-box">
                <li>
                  <h2 style={{ minWidth: 90 }}>제조사</h2>
                  <p>{modelDetailState?.manufacturer}</p>
                </li>
                <li>
                  <h2 style={{ minWidth: 90 }}>총판 이름</h2>
                  <p>{modelDetailState?.distributorName}</p>
                </li>
              </ul>
            </div>
          </InforBox>
          <InforBoxArea1>
            <InforBox>
              <h1>데이터 정보</h1>
              <div className="text-wrap">
                <ul className="text-box">
                  <li>
                    <h2 style={{ minWidth: 104 }}>파서 타입</h2>
                    <p>{modelDetailState?.parserType}</p>
                  </li>
                  <li>
                    <h2 style={{ minWidth: 104 }}>프로토콜</h2>
                    <p>{modelDetailState?.protocol}</p>
                  </li>
                  <li>
                    <h2 style={{ minWidth: 104 }}>DID 검증</h2>
                    <p>{modelDetailState?.isDid ? '여' : '부'}</p>
                  </li>
                </ul>
                <ul className="text-box">
                  <li>
                    <h2 style={{ minWidth: 132 }}>데이터간 구분자</h2>
                    <p>{modelDetailState?.separator}</p>
                  </li>
                  <li>
                    <h2 style={{ minWidth: 132 }}>수집 주기</h2>
                    <p>{modelDetailState?.collectionCycle}</p>
                  </li>
                </ul>
              </div>
            </InforBox>
            <InforBox>
              <div className="text-wrap">
                <ul className="text-box">
                  <li>
                    <h2 style={{ minWidth: 104 }}>디바이스 수</h2>
                    <p>{modelDetailState?.deviceCount.toLocaleString()}</p>
                  </li>
                </ul>
                <ul className="text-box">
                  <li>
                    <h2 style={{ minWidth: 132 }}>데이터 수</h2>
                    <p>{modelDetailState?.dataCount.toLocaleString()}</p>
                  </li>
                </ul>
              </div>
            </InforBox>
          </InforBoxArea1>
        </InforWrap>
      </CntBox1>
      <CntBox1>
        <div className="flex aic jcsb">
          <TitleTextBox text="데이터 정보" />
        </div>
        <InforWrap2>
          {modelDetailState?.dataInformation &&
            Object.entries(modelDetailState.dataInformation).map(
              ([key, info], index) => (
                <InforBox key={index}>
                  <h1>데이터 {index + 1}</h1>
                  <div className="text-wrap">
                    <ul className="text-box">
                      <li>
                        <h2 style={{ minWidth: 118 }}>데이터 명</h2>
                        <p>{key}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>표시 구분</h2>
                        <p>{info.displayClassification}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>데이터 최소값</h2>
                        <p>{info.minData}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>소수점</h2>
                        <p>{info.point}</p>
                      </li>
                    </ul>
                    <ul className="text-box">
                      <li>
                        <h2 style={{ minWidth: 118 }}>데이터 타입</h2>
                        <p>{info.type}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>구간 수</h2>
                        <p>{info.sectionsNumber}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>데이터 최대값</h2>
                        <p>{info.maxData}</p>
                      </li>
                      <li>
                        <h2 style={{ minWidth: 118 }}>데이터 노출</h2>
                        <p>{info.isView ? '여' : '부'}</p>
                      </li>
                    </ul>
                  </div>
                </InforBox>
              ),
            )}
        </InforWrap2>
      </CntBox1>
      {modelReportModalState && <ModelReportModal id={Number(id)} />}
      {modelModifyModalState && <ModelModifyModal id={Number(id)} />}
    </>
  );
};

export default ModelDetail;

export const CntBox1 = styled(CS.CntBoxType1)`
  width: 100%;
  &:nth-child(2) {
    margin-top: 20px;
  }
`;

export const CntBox2 = styled(CS.CntBoxType1)`
  width: 100%;
  margin-top: 20px;
`;

export const InforWrap = styled.div`
  width: 100%;
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

export const InforWrap2 = styled(InforWrap)`
  flex-wrap: wrap;
`;

export const InforBoxArea1 = styled.div`
  width: calc(50% - 10px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  > div {
    width: 100%;
  }
  > div:nth-child(2) .text-wrap {
    padding: 0;
  }
`;

export const InforBox = styled(CS.CntBoxType2)`
  width: calc(50% - 10px);
`;

export const SearchOptionBox = styled(CS.SearchOptionBox)`
  width: 100%;
  margin-top: 20px;
`;

export const InputWrap = styled.div`
  gap: 8px;
  display: flex;
  flex-direction: column;
`;

export const InputArea = styled.div`
  gap: 8px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  > h2 {
    color: #555;
    font-size: 14px;
  }
`;

export const TabCntBox = styled.div`
  width: 100%;
  margin-top: 20px;
`;

export const TableOpitonBox = styled(CS.TableOpitonBox)`
  width: 100%;
  margin-top: 0;
`;

export const TableBox = styled(CS.TableType1)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 32px;
`;

export const MapBox = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`;

export const MapModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 60%;
  transform: translate(-50%, -50%);
  width: 136px;
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  > .title-box {
    box-sizing: border-box;
    padding: 8px 12px;
    background: #e9e8e8;
    font-size: 16px;
    font-weight: 500;
  }
  > .text-box {
    box-sizing: border-box;
    padding: 12px;
    background: #fff;
    > .state-box {
      display: flex;
      align-items: center;
      gap: 8px;
      > .text {
        font-size: 15px;
      }
      > .line {
        width: 1px;
        height: 10px;
        background: #d9d9d9;
      }
    }
    > .date {
      margin-top: 6px;
      font-size: 12px;
      font-weight: 300;
      color: #777;
    }
    > button {
      margin-top: 8px;
      font-size: 14px;
      color: #3c8deb;
    }
  }
`;

export const MapTextModal = styled.div`
  position: absolute;
  z-index: 10;
  top: 50%;
  left: 70%;
  transform: translate(-50%, -50%);
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid #d9d9d9;
  padding: 10px 16px;
  box-shadow: 4px 4px 12px 0px rgba(255, 255, 255, 0.25);
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  > p {
    font-size: 15px;
    white-space: nowrap;
  }
  > button {
    width: 10px;
    min-width: 10px;
  }
`;
